/**
 * Смена номера телефона (R-06, В-30): код уходит на новый номер, после
 * подтверждения старый перестаёт работать сразу. Номер — ID в программе
 * лояльности, поэтому после смены перечитываем и профиль, и карту.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { components } from '@dorixona/api'
import { useApi } from '../../api/context'
import { keys } from '../../api/keys'
import { toCustomer } from '../auth/api'

type ChallengeDto = components['schemas']['OTPChallenge']

export interface PhoneChallenge {
  challengeId: string
  /** Через сколько секунд можно попросить код ещё раз. */
  resendIn: number
}

/** Шаг 1: код на новый номер. Номер чужой — 409 `conflict`. */
export function useRequestPhoneCode() {
  const api = useApi()
  return useMutation({
    mutationFn: async (phone: string) => {
      const dto = await api.post<ChallengeDto>('/me/phone/otp', { phone })
      return { challengeId: dto.challenge_id, resendIn: dto.resend_in } satisfies PhoneChallenge
    },
  })
}

/**
 * Шаг 2: код из СМС. Сервер отвечает профилем уже с новым номером — кладём
 * его в сессию сразу (`['me', true]` — ключ SessionProvider), чтобы ни один
 * экран не показал старый номер, пока идёт повторный запрос.
 */
export function useVerifyPhone() {
  const api = useApi()
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({ challengeId, code }: { challengeId: string; code: string }) =>
      toCustomer(
        await api.post<components['schemas']['Customer']>('/me/phone/verify', {
          challenge_id: challengeId,
          code,
        }),
      ),
    onSuccess: async (customer) => {
      client.setQueryData(['me', true], customer)
      await client.invalidateQueries({ queryKey: ['me'] })
      await client.invalidateQueries({ queryKey: keys.loyalty })
    },
  })
}
